import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import Toast from '../../components/Toast';
import Captcha, { validateCaptcha } from '../../components/Captcha';

const VigilanceLogin = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();

  // State management
  const [loginData, setLoginData] = useState({
    loginType: 'email', // 'email' or 'phone'
    email: '',
    phone: '',
    otp: '',
    verificationCode: '',
    otpToken: null
  });
  const [otpSent, setOtpSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaKey, setCaptchaKey] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [captchaInputOtp, setCaptchaInputOtp] = useState('');
  const [captchaKeyOtp, setCaptchaKeyOtp] = useState('');
  const [refreshTriggerOtp, setRefreshTriggerOtp] = useState(0);

  // Toast state
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const sendOtp = async () => {
    if (isSubmitting) return;

    const login = loginData.loginType === 'email' ? loginData.email : loginData.phone;
    const fieldName = loginData.loginType === 'email' ? 'email' : 'phone';

    if (!login) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: `${loginData.loginType === 'email' ? 'Email' : 'Phone number'} is required`
      }));
      return;
    }

    // Use correct CAPTCHA based on current step
    const currentCaptchaInput = otpSent ? captchaInputOtp : captchaInput;
    const currentCaptchaKey = otpSent ? captchaKeyOtp : captchaKey;

    // Validate CAPTCHA (frontend basic check)
    const captchaValidation = validateCaptcha(currentCaptchaInput);
    if (!captchaValidation.isValid) {
      setErrors(prev => ({
        ...prev,
        [otpSent ? 'captchaOtp' : 'captcha']: captchaValidation.error
      }));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await authService.userSendLoginOtp({
        login,
        captcha: currentCaptchaInput,
        captcha_key: currentCaptchaKey
      });

      // CAPTCHA consumed on attempt, refresh corresponding trigger
      if (otpSent) {
        setRefreshTriggerOtp(prev => prev + 1);
        setCaptchaInputOtp('');
      } else {
        setRefreshTrigger(prev => prev + 1);
        setCaptchaInput('');
      }

      // 🔐 SECURITY: Check for otp_token in response, not just success flag
      // This prevents Burp Suite from faking 200 OK without the actual token
      if (result.success && result.data?.otp_token) {
        setOtpSent(true);
        setLoginData(prev => ({ ...prev, otpToken: result.data.otp_token })); // Store the OTP token
        setErrors(prev => ({ ...prev, [fieldName]: '', captcha: '' }));

        setToast({
          isVisible: true,
          message: `OTP sent to your ${loginData.loginType}`,
          type: 'success'
        });
      } else if (result.success && !result.data?.otp_token) {
        // Security error: Got success but no token (possible manipulation)
        setOtpSent(false);
        setErrors({ general: 'Security Error: Invalid server response. Please try again.' });
      } else {

        setOtpSent(false);

        const currentField = otpSent ? 'captchaOtp' : fieldName;
        // If we expected success but didn't get a token (and no explicit error), treat as error
        const errorMsg = result.error || result.message || 'Failed to send OTP';

        setErrors(prev => ({
          ...prev,
          [currentField]: errorMsg
        }));
      }
    } catch (error) {
      console.error('Send OTP error:', error);

      setOtpSent(false);

      setErrors(prev => ({
        ...prev,
        [fieldName]: 'Failed to send OTP. Please try again.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateLoginForm = () => {
    const newErrors = {};

    if (loginData.loginType === 'email') {
      if (!loginData.email.trim()) newErrors.email = 'Email is required';
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (loginData.email && !emailRegex.test(loginData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else {
      if (!loginData.phone.trim()) newErrors.phone = 'Phone number is required';
      const phoneRegex = /^[6-9]\d{9}$/;
      if (loginData.phone && !phoneRegex.test(loginData.phone)) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
      }
    }

    if (!loginData.otp.trim()) newErrors.otp = 'OTP is required';

    // Validate CAPTCHA (frontend basic check)
    const captchaValidation = validateCaptcha(captchaInputOtp);
    if (!captchaValidation.isValid) {
      newErrors.captchaOtp = captchaValidation.error;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    if (isSubmitting) return;

    if (!validateLoginForm()) return;

    setIsSubmitting(true);
    try {
      const login = loginData.loginType === 'email' ? loginData.email : loginData.phone;

      const result = await authService.userVerifyLoginOtp({
        login: login,
        otp: loginData.otp,
        otp_token: loginData.otpToken, // Send the stored OTP token
        captcha: captchaInputOtp,
        captcha_key: captchaKeyOtp
      });

      if (result.success) {
        // Update AuthContext with user data
        authLogin(result.user, result.token);

        setToast({
          isVisible: true,
          message: 'Login successful! Redirecting to dashboard...',
          type: 'success'
        });

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/vigilance-dashboard');
        }, 1500);
      } else {

        setOtpSent(false);
        setLoginData(prev => ({ ...prev, otp: '' }));
        setRefreshTriggerOtp(prev => prev + 1);
        setCaptchaInputOtp('');

        setErrors({ general: result.error || result.message || 'Login failed. Please try again.' });
      }
    } catch (error) {
      console.error('Login error:', error);

      setOtpSent(false);
      setLoginData(prev => ({ ...prev, otp: '' }));
      setRefreshTriggerOtp(prev => prev + 1);
      setCaptchaInputOtp('');

      setErrors({ general: 'Login failed. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Back to Home Link - Top */}
        <div className="text-left">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300 inline-flex items-center space-x-2"
            aria-label="Back to home"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Back to Home</span>
          </button>
        </div>

        {/* Header */}
        <div className="text-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo192.png`}
            alt="SJVN Limited Logo"
            className="w-18 h-24 mx-auto mb-4"
            role="img"
            aria-label="SJVN Limited Logo"
          />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Vigilance Login</h2>
          <p className="text-gray-600">Access your vigilance account</p>
          <div className="w-24 h-1 bg-primary-600 mx-auto mt-4"></div>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Email/Phone Input Step */}
          {!otpSent && (
            <form onSubmit={(e) => { e.preventDefault(); sendOtp(); }} className="space-y-6" role="form" aria-label="Vigilance Login Form" autoComplete="off">
              {/* Login Method Selection */}
              <div className="space-y-4">
                <label className="text-sm font-medium text-gray-700" htmlFor="login-method">
                  Choose Login Method
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setLoginData(prev => ({ ...prev, loginType: 'email' }));
                      setRefreshTrigger(prev => prev + 1);
                      setCaptchaInput('');
                    }}
                    className={`p-3 rounded-lg border-2 transition-all duration-300 ${loginData.loginType === 'email'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    aria-pressed={loginData.loginType === 'email'}
                    aria-label="Login with email"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium">Email</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setLoginData(prev => ({ ...prev, loginType: 'phone' }));
                      setRefreshTrigger(prev => prev + 1);
                      setCaptchaInput('');
                    }}
                    className={`p-3 rounded-lg border-2 transition-all duration-300 ${loginData.loginType === 'phone'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    aria-pressed={loginData.loginType === 'phone'}
                    aria-label="Login with phone number"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="font-medium">Phone</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Email Input */}
              {loginData.loginType === 'email' && (
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="off"
                    required
                    value={loginData.email}
                    onChange={handleLoginInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter your email address"
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    aria-invalid={errors.email ? 'true' : 'false'}
                  />
                  {errors.email && (
                    <p id="email-error" className="mt-2 text-sm text-red-600" role="alert">
                      {errors.email}
                    </p>
                  )}
                </div>
              )}

              {/* Phone Input */}
              {loginData.loginType === 'phone' && (
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="off"
                    required
                    value={loginData.phone}
                    onChange={handleLoginInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter your 10-digit phone number"
                    maxLength="10"
                    aria-describedby={errors.phone ? 'phone-error' : undefined}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                  />
                  {errors.phone && (
                    <p id="phone-error" className="mt-2 text-sm text-red-600" role="alert">
                      {errors.phone}
                    </p>
                  )}
                </div>
              )}

              {/* CAPTCHA */}
              <Captcha
                value={captchaInput}
                onChange={(e) => {
                  setCaptchaInput(e.target.value);
                  if (errors.captcha) {
                    setErrors(prev => ({ ...prev, captcha: '' }));
                  }
                }}
                error={errors.captcha}
                disabled={isSubmitting}
                onCaptchaChange={(data) => setCaptchaKey(data.key)}
                refreshTrigger={refreshTrigger}
              />

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-md ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:from-primary-700 hover:to-primary-800 hover:shadow-lg'
                  }`}
                aria-label="Send OTP to login"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send OTP'
                )}
              </button>

              {/* Other Options - Inside Login Card */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center">
                  <span>Other options: </span>
                  <button
                    type="button"
                    onClick={() => navigate('/vigilance-validation')}
                    className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
                    aria-label="Validate yourself"
                  >
                    Validate Yourself
                  </button>
                  <span className="mx-2">|</span>
                  <button
                    type="button"
                    onClick={() => navigate('/vigilance-signup')}
                    className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
                    aria-label="Sign up"
                  >
                    Sign Up
                  </button>
                  <span className="mx-2">|</span>
                  <button
                    type="button"
                    onClick={() => navigate('/vigilance-status')}
                    className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
                    aria-label="Check complaint status"
                  >
                    Check Complaint Status
                  </button>
                </p>
              </div>
            </form>
          )}

          {/* OTP Input Step */}
          {otpSent && (
            <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} className="space-y-6" role="form" aria-label="OTP Verification Form" autoComplete="off">
              {/* OTP Input */}
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  OTP send successfully
                </p>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  autoComplete="off"
                  required
                  value={loginData.otp}
                  onChange={handleLoginInputChange}
                  className={`w-full px-4 py-3 border rounded-lg text-center text-2xl font-mono tracking-widest focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${errors.otp ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="000000"
                  maxLength="6"
                  aria-describedby={errors.otp ? 'otp-error' : undefined}
                  aria-invalid={errors.otp ? 'true' : 'false'}
                />
                {errors.otp && (
                  <p id="otp-error" className="mt-2 text-sm text-red-600" role="alert">
                    {errors.otp}
                  </p>
                )}
              </div>

              {/* CAPTCHA for OTP Verification */}
              <Captcha
                value={captchaInputOtp}
                onChange={(e) => {
                  setCaptchaInputOtp(e.target.value);
                  if (errors.captchaOtp) {
                    setErrors(prev => ({ ...prev, captchaOtp: '' }));
                  }
                }}
                error={errors.captchaOtp}
                disabled={isSubmitting}
                onCaptchaChange={(data) => setCaptchaKeyOtp(data.key)}
                refreshTrigger={refreshTriggerOtp}
              />

              {/* Action Buttons */}
              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-md hover:shadow-lg ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:from-primary-700 hover:to-primary-800'
                    }`}
                  aria-label="Verify OTP and login"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    'Verify OTP & Login'
                  )}
                </button>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      setOtpSent(false);
                      setLoginData(prev => ({ ...prev, otp: '' }));
                      setErrors({});
                    }}
                    disabled={isSubmitting}
                    className={`text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    aria-label="Go back to email/phone input"
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={() => sendOtp()}
                    disabled={isSubmitting}
                    className={`text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    aria-label="Resend OTP"
                  >
                    Resend OTP
                  </button>
                </div>
              </div>
            </form>
          )}

        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ isVisible: false, message: '', type: 'success' })}
      />
    </div>
  );
};

export default VigilanceLogin;
